import { lazy, Suspense, useEffect } from 'react';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';

// Below-fold sections — code-split so they don't block initial paint
const ServicesSection = lazy(() => import('./components/ServicesSection'));
const ProductsSection = lazy(() => import('./components/ProductsSection'));
const GallerySection = lazy(() => import('./components/GallerySection'));
const AboutSection = lazy(() => import('./components/AboutSection'));
const FaqSection = lazy(() => import('./components/FaqSection'));
const ContactSection = lazy(() => import('./components/ContactSection'));
const Footer = lazy(() => import('./components/Footer'));

function SectionFallback() {
  return <div className="w-full py-24 bg-white" aria-hidden="true" />;
}

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: 'L.M. Webber Enterprises LLC',
  image: 'https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/fa2dd6dc20055f8052bf5cbdd4a60cb4.png',
  '@id': `${siteUrl}/#localbusiness`,
  url: siteUrl,
  telephone: '+1-860-212-6316',
  email: 'info@lmwebber.com',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Union',
    addressRegion: 'CT',
    postalCode: '06076',
    addressCountry: 'US',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 41.9887,
    longitude: -72.1756,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
      ],
      opens: '06:00',
      closes: '18:00',
    },
  ],
  priceRange: '$$',
  areaServed: {
    '@type': 'GeoCircle',
    geoMidpoint: {
      '@type': 'GeoCoordinates',
      latitude: 41.9887,
      longitude: -72.1756,
    },
    geoRadius: '100 miles',
  },
  serviceType: [
    'Bulk Water Delivery',
    'Pool Water Delivery',
    'Construction Material Sales',
    'Dust Control Trailer Rental',
    'Emergency Water Supply',
  ],
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'L.M. Webber Enterprises LLC | Bulk Water Delivery & Construction Materials',
  description:
    'L.M. Webber Enterprises LLC provides bulk water delivery for pools, spas, construction sites, and fire departments. We also offer dump trailer services and material sales including sand, stone, mulch, and salt.',
  url: siteUrl,
  mainEntity: {
    '@id': `${siteUrl}/#localbusiness`,
  },
};

export default function Home() {
  useEffect(() => {
    // Inject Schema.org JSON-LD
    const addScript = (id: string, data: object) => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    };

    addScript('schema-localbusiness', localBusinessSchema);
    addScript('schema-webpage', webPageSchema);

    return () => {
      document.getElementById('schema-localbusiness')?.remove();
      document.getElementById('schema-webpage')?.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <HeroSection />
        <Suspense fallback={<SectionFallback />}>
          <ServicesSection />
        </Suspense>
        <Suspense fallback={<SectionFallback />}>
          <ProductsSection />
        </Suspense>
        <Suspense fallback={<SectionFallback />}>
          <GallerySection />
        </Suspense>
        <Suspense fallback={<SectionFallback />}>
          <AboutSection />
        </Suspense>
        <Suspense fallback={<SectionFallback />}>
          <FaqSection />
        </Suspense>
        <Suspense fallback={<SectionFallback />}>
          <ContactSection />
        </Suspense>
      </main>
      <Suspense fallback={<SectionFallback />}>
        <Footer />
      </Suspense>
    </div>
  );
}
